/**
 * Deterministic market simulation, independent of React/DOM.
 *
 * Time: all times are "sim ms" supplied by the caller via advance(to). Nothing reads a wall clock.
 * Ordering at identical timestamps (documented, tested):
 *   1. scheduled scenario events (by t, then insertion seq)
 *   2. order landings (by landAt, then order seq)
 *   3. tick work at TICK_MS grid boundaries: lifecycle, background spawn, bot trades (token creation order)
 * Randomness: background spawning uses its own stream advanced once per tick; each token owns a stream advanced
 * only by its own tick work. Player/peer actions never draw from these streams, so UI, polling frequency and user
 * interactions cannot change the background market.
 */
import { FEE_MODES, PLATFORMS, SIM, type Pair, type Platform } from './config';
import { genCoinName, pickNarrative } from './narratives';
import { noiseArt } from './artmix';
import { Director } from './director';
import { KOL_BY_ID, KOL_CFG, KOLS_ALL, kolOfWallet, kolWallet } from './kols';
import { baseRate, buyProb, FLOW, jump, newFlow, nextRegime, orderSize, poisson, setTier, stepAttention, walletType, type FlowState, type Tier } from './flow';
import { ammPool, bonding, INITIAL, isInt, mcSol, mcUsd, priceSol, quoteBuy, quoteSell, solToLamports, type Reserves } from './curve';
import { fakeAddr, hashStr, Rng } from './rng';
import type { Candle, DeployRequest, Fate, Holder, LimitOrder, Order, Position, RunPlan, ScenarioEvent, TokenSpec, TokenState, Trade, Column } from './types';

export type MarketEvent =
  | { type: 'token:new'; token: TokenState }
  | { type: 'token:phase'; token: TokenState; from: string; to: string }
  | { type: 'order'; order: Order }
  | { type: 'deploy'; ok: boolean; token?: TokenState; reason?: string; requestId: string; landAt: number; roundId?: string }
  | { type: 'trade'; trade: Trade }
  | { type: 'scenario'; ev: ScenarioEvent; roundId: string }
  | { type: 'wallet' };

interface Scheduled { t: number; seq: number; roundId: string; ev: ScenarioEvent }
interface PendingDeploy { id: string; req: DeployRequest; total: number; submittedAt: number; landAt: number; roundId?: string; seq: number; tokenId?: string }
interface FateParams { mul: number; peak: number; life: number; act: number; m0: number; rugAt?: number; rugged?: boolean; snipes?: { at: number; sol: number; tag: Holder['tag'] }[] }

export interface MarketOptions { seed: number; background?: boolean; startBalanceSol?: number; now?: number; latencyMs?: number }

// background fates only: 'runner' is reserved for catalyst-linked coins (see catalyst.ts)
const FATES: [Fate, number][] = [['weak', 38], ['grind', 20], ['dump', 15], ['rug', 12], ['buyback', 15]];
const PLAT_W: [Platform, number][] = [['pump', 74], ['bonk', 12], ['bags', 6], ['moonshot', 3], ['believe', 2], ['jupstudio', 3]];
const PAIR_W: [Pair, number][] = [['SOL', 86], ['SPACEX', 8], ['TESLA', 6]];

export class Market {
  readonly tokens = new Map<string, TokenState>();
  readonly cols: Record<Column, string[]> = { new: [], final: [], migrated: [] };
  readonly positions = new Map<string, Position>();
  balance: number; // available lamports
  reserved = 0;
  orders: Order[] = []; // pending
  orderLog: Order[] = []; // recent finished, bounded
  deploys: PendingDeploy[] = [];
  globalTrades: Trade[] = [];
  time: number;
  latencyMs: number;
  background: boolean;
  /** When set (multiplayer), orders on these tokens are forwarded instead of settled locally. */
  remote: { tokens: Set<string>; submit: (o: Order) => void } | null = null;
  frozen = false;
  version = 0;
  readonly changed = new Set<string>();
  private listeners = new Set<(e: MarketEvent) => void>();
  private schedule: Scheduled[] = [];
  private seq = 0;
  private orderSeq = 0;
  private bgRng: Rng;
  private bgCount = 0;
  private nextTick: number;
  private fp = new Map<string, FateParams>();
  private tokRng = new Map<string, Rng>();
  private walletRng: Rng;
  private agentBal = new Map<string, number>();
  /** background order-flow state (only for flow-driven tokens) */
  readonly flows = new Map<string, FlowState>();
  /** narrative scheduler (free play / background markets only) */
  director: Director | null = null;
  limits: LimitOrder[] = [];

  constructor(opts: MarketOptions) {
    this.bgRng = new Rng(opts.seed).derive('bg');
    this.walletRng = new Rng(opts.seed).derive('wallets');
    this.balance = solToLamports(opts.startBalanceSol ?? SIM.START_BALANCE_SOL);
    this.time = opts.now ?? 0;
    this.nextTick = Math.ceil(this.time / SIM.TICK_MS) * SIM.TICK_MS;
    this.latencyMs = opts.latencyMs ?? SIM.DEFAULT_LATENCY_MS;
    this.background = opts.background ?? true;
    if (this.background) { this.seedMature(new Rng(opts.seed).derive('mature')); this.director = new Director(opts.seed, this.time, (evs) => this.scheduleEvents('director', 0, evs)); }
  }

  /** Move a completed curve into a real-reserve AMM pool holding the unsold supply (0.25% fee). */
  private toPool(tok: TokenState) {
    let held = 0; for (const h of tok.holders.values()) held += h.raw;
    // a single huge buy can overshoot completion; keep the pool solvent in that rare case
    tok.reserves = ammPool(Math.max(SIM.TOTAL_SUPPLY_RAW - held, SIM.TOTAL_SUPPLY_RAW * 0.02)); this.changed.add(tok.id); this.version++;
  }

  /** Plausible pre-existing migrated coins (ages/market caps vary). Their pre-session history is not fabricated:
   *  trades/candles start at session time; only reserves/holders reflect the past. */
  private seedMature(r: Rng) {
    for (let i = 0; i < 8; i++) {
      const spec = { ...this.genNoise(r), id: `m${i}`, fate: 'grind' as Fate, devBuySol: 0, sniperWave: false, tier: 'mature' as const };
      const tok = this.createToken(spec, this.time - r.range(15 * 60_000, 6 * 3600_000));
      const target = r.range(70_000, 450_000); let j = 0;
      const fill = () => { const f = this.execute(tok, 'buy', solToLamports(r.range(1, 6))); const w = `${tok.id}:h${j++ % 220}`; const h = tok.holders.get(w) ?? { wallet: w, raw: 0, cost: 0 }; h.raw += f.raw; h.cost = (h.cost ?? 0) + f.lamports; tok.holders.set(w, h); tok.txCount++; tok.buys++; tok.volumeLamports += f.lamports; };
      while (bonding(tok.reserves) < 100 && j < 4000) fill();
      this.toPool(tok);
      while (mcUsd(tok.reserves) < target && j < 8000) fill();
      tok.phase = 'migrated'; tok.migratedAt = tok.createdAt + 60_000; tok.trades = []; tok.candles = []; tok.athMc = mcUsd(tok.reserves);
      const p = this.fp.get(tok.id)!; p.m0 = mcUsd(tok.reserves); p.mul = 1.3; p.peak = 120_000; p.life = 3600_000; p.act = 0.7;
      for (const c of ['new', 'final'] as Column[]) { const k = this.cols[c].indexOf(tok.id); if (k >= 0) this.cols[c].splice(k, 1); }
      this.cols.migrated.push(tok.id);
    }
  }

  on(fn: (e: MarketEvent) => void): () => void { this.listeners.add(fn); return () => this.listeners.delete(fn); }
  private emit(e: MarketEvent) { for (const l of this.listeners) l(e); }

  // ------------------------------------------------------------------ scenario scheduling
  scheduleEvents(roundId: string, start: number, events: ScenarioEvent[]) {
    for (const ev of events) this.schedule.push({ t: start + ev.t, seq: this.seq++, roundId, ev });
    this.schedule.sort((a, b) => a.t - b.t || a.seq - b.seq);
  }
  /** Cancel pending scheduled events (and pending orders) of a round. */
  cancelRound(roundId: string) {
    this.schedule = this.schedule.filter((s) => s.roundId !== roundId);
    for (const o of [...this.orders]) if (o.roundId === roundId) this.finishOrder(o, 'cancelled', 'round ended');
    for (const d of [...this.deploys]) if (d.roundId === roundId) { this.deploys = this.deploys.filter((x) => x !== d); this.release(d.total); this.emit({ type: 'deploy', ok: false, reason: 'round ended', requestId: d.id, landAt: d.landAt, roundId }); }
  }
  pendingScheduled(roundId?: string) { return this.schedule.filter((s) => !roundId || s.roundId === roundId).length; }

  // ------------------------------------------------------------------ clock
  advance(to: number) {
    if (to < this.time) return;
    for (;;) {
      const nS = this.schedule[0]?.t ?? Infinity;
      const nO = this.nextOrderTime();
      const nT = this.nextTick;
      const next = Math.min(nS, nO, nT);
      if (next > to) break;
      this.time = next;
      if (nS === next) { const s = this.schedule.shift()!; this.runScenario(s); continue; }
      if (nO === next) { this.landNext(); continue; }
      this.tick(next);
      this.nextTick = next + SIM.TICK_MS;
    }
    this.time = to;
  }

  private nextOrderTime(): number {
    let m = Infinity;
    for (const o of this.orders) if (o.status === 'pending' && o.landAt < m && !(this.remote?.tokens.has(o.tokenId))) m = o.landAt;
    for (const d of this.deploys) if (d.landAt < m) m = d.landAt;
    return m;
  }

  private landNext() {
    let best: { kind: 'o'; o: Order; t: number; s: number } | { kind: 'd'; d: PendingDeploy; t: number; s: number } | null = null;
    for (const o of this.orders) if (o.status === 'pending' && !(this.remote?.tokens.has(o.tokenId))) { const s = Number(o.id.split(':')[1]); if (!best || o.landAt < best.t || (o.landAt === best.t && s < best.s)) best = { kind: 'o', o, t: o.landAt, s }; }
    for (const d of this.deploys) if (!best || d.landAt < best.t || (d.landAt === best.t && d.seq < best.s)) best = { kind: 'd', d, t: d.landAt, s: d.seq };
    if (!best) return;
    if (best.kind === 'o') this.settle(best.o); else this.landDeploy(best.d);
  }

  private runScenario(s: Scheduled) {
    const ev = s.ev;
    if (ev.kind === 'launch' || ev.kind === 'vampBot') this.createToken(ev.token, this.time);
    else if (ev.kind === 'kol') { const t = this.tokens.get(ev.tokenId); if (t) this.kolTrade(t, ev.side, ev.kolId, ev.sol, ev.fraction ?? 1); }
    this.emit({ type: 'scenario', ev, roundId: s.roundId });
  }

  // ------------------------------------------------------------------ token creation
  createToken(spec: TokenSpec, at: number, fateOverride?: Partial<FateParams>): TokenState {
    const existing = this.tokens.get(spec.id); if (existing) return existing;
    const tok: TokenState = {
      ...spec, createdAt: at, reserves: { ...INITIAL }, realSolIn: 0, phase: 'new', volumeLamports: 0, feesLamports: 0, txCount: 0, buys: 0, sells: 0,
      holders: new Map(), trades: [], candles: [], athMc: 0, kolCount: 0, proCount: 0, lastActivity: at,
    };
    this.tokens.set(tok.id, tok);
    const rng = new Rng(hashStr(spec.id + ':' + spec.address));
    this.tokRng.set(tok.id, rng);
    if (spec.devBuySol > 0) this.botTrade(tok, 'buy', solToLamports(spec.devBuySol), 'dev:' + spec.devAddress, 'dev', 'dev');
    if (spec.bundleSol) for (let i = 0; i < 3; i++) this.botTrade(tok, 'buy', solToLamports(spec.bundleSol / 3), `${tok.id}:bundle${i}`, 'bundle', 'bundler');
    const m0 = mcUsd(tok.reserves);
    const p = this.drawFate(rng, spec, m0, at);
    const tier = spec.tier ?? (this.background && spec.role === 'noise' && !spec.catalyst ? 'dead' : undefined);
    if (tier === 'hot' && this.counts().hot >= FLOW.hotMax) {
      // a narrative caught: the weakest non-canonical hot coin starts dying instead (keeps 2-3 hot coins alive)
      let weakest: [string, FlowState] | null = null;
      for (const [id, f] of this.flows) { const tk = this.tokens.get(id); if (tk && f.tier === 'hot' && !f.death && (!weakest || f.attention < weakest[1].attention)) weakest = [id, f]; }
      if (weakest) weakest[1].death = { mode: 'bleed', until: at + 60000 };
    }
    if (tier) this.flows.set(tok.id, newFlow(rng.derive('flow'), tier, at));
    Object.assign(p, fateOverride);
    this.fp.set(tok.id, p);
    tok.athMc = m0;
    this.cols.new.unshift(tok.id);
    this.trimColumn('new');
    this.changed.add(tok.id); this.version++;
    this.emit({ type: 'token:new', token: tok });
    this.pruneTokens();
    return tok;
  }

  private drawFate(rng: Rng, spec: TokenSpec, m0: number, at: number): FateParams {
    const f = spec.fate;
    const base: FateParams = { mul: 1, peak: 8000, life: 90000, act: 1, m0 };
    if (f === 'runner') Object.assign(base, { mul: spec.runMul ?? rng.range(6, 30), peak: rng.range(20000, 45000), life: rng.range(60000, 150000), act: 2.2 });
    else if (f === 'weak') Object.assign(base, { mul: rng.range(1.05, 1.6), peak: rng.range(3000, 10000), life: rng.range(20000, 45000), act: 0.6 });
    else if (f === 'rug') Object.assign(base, { mul: rng.range(2, 6), peak: rng.range(8000, 30000), life: 60000, act: 1.4 });
    else if (f === 'grind') Object.assign(base, { mul: rng.range(3, 12), peak: rng.range(90000, 220000), life: 240000, act: 0.9 });
    else if (f === 'dump') Object.assign(base, { mul: rng.range(1.8, 4), peak: rng.range(3000, 8000), life: 30000, act: 1.5 });
    else if (f === 'buyback') Object.assign(base, { mul: rng.range(2, 6), peak: rng.range(6000, 15000), life: 90000, act: 1.3 });
    if (spec.runMul && f !== 'runner') base.mul = spec.runMul;
    if (!spec.catalyst) {
      // background regime: no explosive runs regardless of requested fate/multiple
      if (f === 'runner') { base.act = 1; base.peak = rng.range(20000, 60000); }
      base.mul = Math.min(base.mul, SIM.BG_MAX_MUL * 0.85);
    }
    if (f === 'rug') base.rugAt = at + base.peak + rng.range(500, 4000);
    if (spec.sniperWave) {
      base.snipes = [];
      const n = rng.int(4, 9);
      for (let i = 0; i < n; i++) base.snipes.push({ at: at + rng.range(30, 1500), sol: rng.range(0.5, 4), tag: 'sniper' });
      base.snipes.sort((a, b) => a.at - b.at);
    }
    return base;
  }

  targetMc(tok: TokenState, t: number): number {
    const p = this.fp.get(tok.id)!; const age = t - tok.createdAt; const m0 = p.m0; const k = Math.max(age, 0) / p.peak;
    switch (tok.fate) {
      case 'runner': return age < p.peak ? m0 * Math.pow(p.mul, k) : m0 * p.mul * Math.max(0.35, 1 - 0.65 * (age - p.peak) / p.life);
      case 'weak': return age < p.peak ? m0 * (1 + (p.mul - 1) * k) : m0 * Math.max(0.65, p.mul - (age - p.peak) / p.life);
      case 'rug': return p.rugged ? m0 * 0.4 : m0 * Math.pow(p.mul, Math.min(1, k));
      case 'grind': return m0 * (1 + (p.mul - 1) * Math.min(1, k));
      case 'dump': return age < p.peak ? m0 * Math.pow(p.mul, k) : m0 * p.mul * Math.max(0.25, 1 - (age - p.peak) / p.life);
      case 'buyback': {
        if (age < p.peak) return m0 * Math.pow(p.mul, k);
        if (age < 2 * p.peak) return m0 * p.mul * (1 - 0.5 * (age - p.peak) / p.peak);
        return m0 * p.mul * Math.min(0.95, 0.5 + 0.45 * (age - 2 * p.peak) / p.peak);
      }
    }
  }

  /** Install an accelerated run. Only allowed for catalyst-linked tokens (eligible Elon/Trump image post). */
  setRun(tokenId: string, plan: RunPlan, catalystPostId?: string): boolean {
    const tok = this.tokens.get(tokenId); const p = this.fp.get(tokenId); if (!tok || !p) return false;
    if (!catalystPostId) return false;
    tok.catalyst = catalystPostId;
    tok.fate = 'runner'; tok.plan = plan; p.mul = plan.mul; p.peak = plan.peakAt; p.life = plan.endAt; p.act = 3; p.m0 = mcUsd(tok.reserves);
    tok.createdAt = Math.min(tok.createdAt, this.time);
    return true;
  }

  // ------------------------------------------------------------------ tick
  private tick(t: number) {
    if (this.frozen) return;
    // lifecycle
    for (const tok of this.tokens.values()) {
      if (tok.phase === 'migrating' && tok.migrateAt !== undefined && t >= tok.migrateAt) {
        tok.phase = 'migrated'; tok.migratedAt = t; this.toPool(tok); this.moveCol(tok, 'migrated'); this.emit({ type: 'token:phase', token: tok, from: 'migrating', to: 'migrated' });
      }
    }
    // background spawn
    if (this.background) {
      const r = this.bgRng;
      this.director?.step(t);
      if (r.next() < SIM.BG_SPAWN_P) { const spec = this.genNoise(r); spec.tier = this.drawTier(r); this.createToken(spec, t); }
      this.superviseHot(t);
    }
    // limit-order triggers (checked on the tick grid, before bot flow)
    if (this.limits.length) this.checkLimits();
    // agent activity
    for (const tok of this.tokens.values()) {
      if (tok.phase === 'dead' || tok.phase === 'migrating') continue;
      const rng = this.tokRng.get(tok.id)!; const p = this.fp.get(tok.id)!;
      const age = t - tok.createdAt;
      if (p.snipes) while (p.snipes.length && p.snipes[0]!.at <= t) { const s = p.snipes.shift()!; this.botTrade(tok, 'buy', solToLamports(s.sol), `${tok.id}:sn${p.snipes.length}`, 'sniper', s.tag); }
      const fl = this.flows.get(tok.id);
      if (fl) { this.flowTick(tok, fl, rng, t); continue; }
      if (p.rugAt && !p.rugged && t >= p.rugAt) { p.rugged = true; this.rug(tok); continue; }
      const decay = age > p.life * 2.5 ? 0.12 : age > p.life ? 0.45 : 1;
      const lambda = 0.9 * p.act * decay;
      let n = 0; let x = rng.next(); const L = Math.exp(-lambda); let prod = x; while (prod > L && n < 8) { n++; x = rng.next(); prod *= x; }
      const cur = mcUsd(tok.reserves);
      let target = this.targetMc(tok, t);
      if (!tok.catalyst) {
        // background budget: future autonomous flow steers back under BG_MAX_MUL x launch MC (fills are never reversed)
        const cap = p.m0 * SIM.BG_MAX_MUL;
        if (target > cap) target = cap;
        if (cur > cap) target = Math.min(target, cap * 0.8);
        // gentle random walk so background coins rally/dip/consolidate instead of tracking a smooth line
        target *= Math.exp(Math.sin(t / (9000 + (hashStr(tok.id) % 7000)) + (hashStr(tok.id) % 6)) * 0.12);
      }
      void cur;
      const recent = this.recentReturn(tok, 10);
      // gap is re-measured before every order so several fills in one tick cannot overshoot the steering target
      for (let i = 0; i < n; i++) this.agentTrade(tok, rng, Math.log(target / mcUsd(tok.reserves)), recent, age);
      // background KOL flow: small trades only (the large showcase/follower chains are catalyst-only, scheduled by scenarios)
      if (rng.next() < KOL_CFG.backgroundTradeProb * (tok.catalyst ? 3 : 1)) {
        const k = KOLS_ALL[Math.floor(rng.next() * KOLS_ALL.length)]!; const w = kolWallet(k.id); const h = tok.holders.get(w);
        if (h && h.raw > 0 && rng.next() < 0.5) this.kolTrade(tok, 'sell', k.id, 0, rng.range(0.3, 1));
        else this.kolTrade(tok, 'buy', k.id, Math.min(KOL_CFG.backgroundMaxSol, rng.range(0.1, k.size[1])), 1);
      }
      if (n) tok.lastActivity = t;
      if (tok.role === 'noise' && age > p.life * 3 && t - tok.lastActivity > 20000 && !this.positions.get(tok.id)?.raw) this.kill(tok);
    }
  }

  // ------------------------------------------------------------------ background flow (flow.ts)
  private counts() {
    let hot = 0, slow = 0;
    for (const [id, f] of this.flows) { const tk = this.tokens.get(id); if (!tk || tk.phase === 'dead') continue; if (f.tier === 'hot' && !f.death) hot++; else if (f.tier === 'slowcook') slow++; }
    return { hot, slow };
  }
  private drawTier(r: Rng): Tier {
    const { hot, slow } = this.counts();
    let tier = r.weighted(FLOW.tierShare);
    if (tier === 'hot' && hot >= FLOW.hotMax) tier = 'slowcook';
    if (tier === 'slowcook' && slow >= FLOW.slowMax) tier = 'dead';
    if (tier === 'dead' && slow < FLOW.slowMin && r.next() < 0.3) tier = 'slowcook';
    return tier;
  }
  /** Keep 2-3 hot coins alive: when short, a narrative "catches" (best slowcook, else the newest launch). */
  private superviseHot(t: number) {
    let { hot } = this.counts();
    while (hot < FLOW.hotMin) {
      let best: string | null = null; let score = -1;
      for (const [id, f] of this.flows) { const tk = this.tokens.get(id); if (!tk || tk.phase === 'dead' || tk.phase === 'migrating') continue; if (f.tier === 'slowcook' && f.attention > score) { score = f.attention; best = id; } }
      if (!best) { let newest = -Infinity; for (const [id, f] of this.flows) { const tk = this.tokens.get(id); if (tk && f.tier === 'dead' && tk.phase === 'new' && tk.createdAt > newest) { newest = tk.createdAt; best = id; } } }
      if (!best) return;
      setTier(this.tokRng.get(best)!, this.flows.get(best)!, 'hot', t); hot++;
    }
  }
  private flowTick(tok: TokenState, f: FlowState, rng: Rng, t: number) {
    const dt = SIM.TICK_MS;
    stepAttention(rng, f, dt);
    if ((f.tier === 'hot' || f.tier === 'slowcook' || f.tier === 'mature') && t >= f.regimeUntil) nextRegime(rng, f, t);
    // price-action attention jumps (a +30% 1s candle)
    const last = tok.candles[tok.candles.length - 1];
    if (last && last.o > 0 && last.c / last.o >= 1.3 && t > f.jumpCooldown && f.tier !== 'dead') { jump(f, f.tier === 'hot' ? 0.12 : 0.2); f.jumpCooldown = t + 20000; }
    // hot death: bleed / dev-insider dump / final fake pump, then near-zero activity
    if (f.tier === 'hot' && !f.death && f.attention < FLOW.deathAttention) {
      const mode = rng.weighted([['bleed', 45], ['dump', 30], ['fakepump', 25]] as const);
      f.death = { mode, until: t + (mode === 'bleed' ? 60000 : mode === 'dump' ? 6000 : 30000) };
    }
    if (f.death) {
      if ((f.death.mode === 'dump' && !f.death.dumped) || (f.death.mode === 'fakepump' && !f.death.dumped && t >= f.death.until - 15000)) {
        f.death.dumped = true;
        // one or two huge sells sized for a -50..80% move (constant product: tokens in = vTok * (1/sqrt(1-drop) - 1))
        const drop = rng.range(0.5, 0.8); let need = Math.floor(tok.reserves.vTok * (1 / Math.sqrt(1 - drop) - 1));
        const big = [...tok.holders.values()].filter((h) => !h.wallet.startsWith('player') && !h.wallet.startsWith('peer:') && !h.wallet.startsWith('kol:')).sort((a, b) => b.raw - a.raw).slice(0, 2);
        for (const h of big) { if (need <= 0) break; const amt = Math.min(h.raw, need); need -= amt; this.botTrade(tok, 'sell', amt, h.wallet, h.tag === 'dev' ? 'dev' : 'bot', h.tag); }
      }
      if (t >= f.death.until) { f.death = undefined; f.diedAt = t; setTier(rng, f, 'faded', t); }
    }
    // arrivals: Poisson(base*attention^1.5 + Hawkes) per tick, capped
    const n = Math.min(40, poisson(rng, (baseRate(f, t) + f.hawkes) * dt / 1000));
    for (let i = 0; i < n; i++) {
      if (f.tier === 'dead') { if (f.deadLeft <= 0) break; f.deadLeft--; }
      if (rng.next() < buyProb(f, t)) {
        const wt = walletType(rng, f); const sol = orderSize(rng, wt);
        const w = wt === 'retail' ? `${tok.id}:w${rng.int(0, 199)}` : wt === 'degen' ? `${tok.id}:d${rng.int(0, 59)}` : `${tok.id}:W${rng.int(0, 9)}`;
        this.botTrade(tok, 'buy', solToLamports(sol), w, 'bot', t - tok.createdAt < 2000 ? 'sniper' : wt === 'whale' ? 'pro' : undefined);
      } else {
        const h = this.pickSeller(tok, rng);
        if (h) this.botTrade(tok, 'sell', this.rawForSol(tok, h.raw, orderSize(rng, walletType(rng, f)) * (f.regime === 'capit' ? 1.8 : 1)), h.wallet, 'bot');
      }
      f.hawkes += f.alpha / f.tauS;
    }
    if (n) tok.lastActivity = t;
    if (f.tier !== 'dead' && f.tier !== 'faded' && !f.death) this.reactions(tok, f, rng, t);
    if (t >= f.removeAt && !this.positions.get(tok.id)?.raw && !this.positions.get(tok.id)?.reservedRaw && tok.role === 'noise') this.kill(tok);
  }
  /** Tokens worth ~`sol` at the current price, capped by the holder's bag (sells are sized like buys, not whole bags). */
  private rawForSol(tok: TokenState, bag: number, sol: number): number {
    const perRaw = priceSol(tok.reserves) / 10 ** SIM.TOKEN_DECIMALS; // SOL per raw unit
    return Math.max(1, Math.min(bag, Math.floor(sol / Math.max(perRaw, 1e-30))));
  }
  /** Bots react to PRICE ACTION (no hidden target): trend followers, profit takers (1.5-4x), panic sellers (-25%). */
  private reactions(tok: TokenState, f: FlowState, rng: Rng, t: number) {
    const r5 = this.recentReturn(tok, 5);
    // trend followers: a short burst after a green move, then they wait (no runaway cascade)
    if (r5 > 0.08 && t >= (f.trendCooldown ?? 0)) { f.trendCooldown = t + rng.range(3000, 8000); for (let k = rng.int(1, 4); k > 0; k--) this.botTrade(tok, 'buy', solToLamports(orderSize(rng, 'degen')), `${tok.id}:tf${rng.int(0, 19)}`, 'bot'); }
    // panic sellers: after a -25% candle a few holders dump part of their bags, then the panic is spent for a while
    const last = tok.candles[tok.candles.length - 1];
    if ((r5 < -0.25 || (last && last.o > 0 && last.c / last.o <= 0.75)) && t >= (f.panicCooldown ?? 0)) {
      f.panicCooldown = t + rng.range(6000, 15000);
      for (let k = rng.int(2, 5); k > 0; k--) { const h = this.pickSeller(tok, rng); if (h) this.botTrade(tok, 'sell', this.rawForSol(tok, Math.floor(h.raw * rng.range(0.3, 0.8)), orderSize(rng, 'degen') * 2), h.wallet, 'bot'); }
    }
    if (rng.next() < 0.3) {
      const priceRaw = priceSol(tok.reserves) / 1e6 * SIM.LAMPORTS;
      for (let k = 0; k < 6; k++) {
        const h = this.pickSeller(tok, rng); if (!h || !h.cost) continue;
        const target = 1.5 + (hashStr(h.wallet) % 250) / 100; // each holder has its own 1.5-4x exit
        if (h.raw * priceRaw >= h.cost * target) { this.botTrade(tok, 'sell', this.rawForSol(tok, Math.floor(h.raw * rng.range(0.3, 1)), orderSize(rng, 'degen') * 3), h.wallet, 'bot'); break; }
      }
    }
    void t;
  }

  /** Fractional change of close price over the last `sec` seconds of candles. */
  private recentReturn(tok: TokenState, sec: number): number {
    const c = tok.candles; if (c.length < 2) return 0;
    const last = c[c.length - 1]!; let ref = last;
    for (let i = c.length - 1; i >= 0 && c[i]!.t >= last.t - sec; i--) ref = c[i]!;
    return ref.o > 0 ? last.c / ref.o - 1 : 0;
  }

  /** Finite simulated balance for an agent wallet (lazy, deterministic from the wallet id). */
  private agentBalance(w: string): number {
    let b = this.agentBal.get(w);
    if (b == null) {
      const h = hashStr(w); const kind = w.includes(':mm') ? 60 : w.includes(':tf') ? 25 : w.includes(':sn') ? 15 : /:W\d+$/.test(w) ? 100 + (h % 300) : /:d\d+$/.test(w) ? 20 + (h % 100) : 3 + (h % 40);
      const k = kolOfWallet(w);
      b = solToLamports(k ? k.balanceSol : kind); this.agentBal.set(w, b);
    }
    return b;
  }

  /** One simulated order from a heterogeneous agent population (retail / market maker / trend follower / profit taker). */
  private agentTrade(tok: TokenState, rng: Rng, gap: number, recent: number, age: number) {
    const bias = Math.max(-0.42, Math.min(0.42, gap * 1.6));
    const scale = 0.06 * Math.pow(mcSol(tok.reserves), 0.6) * (tok.catalyst ? (1 + Math.min(4, Math.abs(gap) * 6)) * 1.6 : 1 + Math.min(1.5, Math.abs(gap) * 3));
    const roll = rng.next();
    if (roll < 0.14) { // market maker: two-sided, small, leans against the gap, respects inventory + funds
      const w = `${tok.id}:mm`; const h = tok.holders.get(w); const size = solToLamports(scale * 0.4 * rng.range(0.5, 1.5));
      if (gap > 0 || !h || h.raw <= 0) this.botTrade(tok, 'buy', size, w, 'mm', 'mm');
      else this.botTrade(tok, 'sell', Math.floor(h.raw * rng.range(0.1, 0.35)), w, 'mm', 'mm');
      return;
    }
    if (roll < 0.24) { // trend follower: chases recent moves, exits on reversals
      const w = `${tok.id}:tf${rng.int(0, 6)}`; const h = tok.holders.get(w);
      if (recent > 0.04 && (tok.catalyst || gap > 0)) this.botTrade(tok, 'buy', solToLamports(scale * rng.range(0.5, 2)), w, 'bot');
      else if (recent < -0.04 && h && h.raw > 0) this.botTrade(tok, 'sell', h.raw, w, 'bot');
      return;
    }
    if (roll < 0.34) { // profit taker: a holder in profit sells part of the bag
      const priceRaw = priceSol(tok.reserves) / 1e6 * SIM.LAMPORTS;
      for (const h of tok.holders.values()) {
        if (h.wallet.startsWith('player') || h.wallet.startsWith('peer:') || h.wallet.startsWith('kol:') || !h.cost || h.raw <= 0) continue;
        if (h.raw * priceRaw > h.cost * 1.6 && rng.next() < 0.5) { this.botTrade(tok, 'sell', Math.floor(h.raw * rng.range(0.3, 1)), h.wallet, 'bot'); return; }
      }
    }
    // retail
    const buy = rng.next() < 0.5 + bias;
    if (buy) {
      const wi = rng.int(0, 60); const w = `${tok.id}:w${wi}`;
      this.botTrade(tok, 'buy', Math.max(1_000_000, solToLamports(-Math.log(1 - rng.next() * 0.999) * scale)), w, 'bot', age < 2000 ? 'sniper' : wi === 7 ? 'insider' : wi === 11 ? 'pro' : undefined);
    } else {
      const h = this.pickSeller(tok, rng);
      if (h) this.botTrade(tok, 'sell', Math.max(1, Math.floor(h.raw * rng.range(0.25, 1))), h.wallet, kolOfWallet(h.wallet) ? 'kol' : h.tag === 'mm' ? 'mm' : 'bot', undefined, kolOfWallet(h.wallet)?.sim);
    }
  }

  /** Pick a selling holder, weighted by bag size (big bags trade more), excluding player/peer/KOL wallets and dust. */
  private pickSeller(tok: TokenState, rng: Rng): Holder | undefined {
    const minRaw = 0.005 / Math.max(priceSol(tok.reserves), 1e-18) * 1e6; // skip dust (< 0.005 SOL)
    let total = 0; const arr: Holder[] = [];
    for (const h of tok.holders.values()) if (h.raw > minRaw && !h.wallet.startsWith('player') && !h.wallet.startsWith('peer:') && !h.wallet.startsWith('kol:')) { arr.push(h); total += h.raw; }
    if (!arr.length) return undefined;
    let x = rng.next() * total;
    for (const h of arr) { x -= h.raw; if (x <= 0) return h; }
    return arr[arr.length - 1];
  }

  private rug(tok: TokenState) {
    for (const h of [...tok.holders.values()]) if ((h.tag === 'dev' || h.tag === 'insider' || h.tag === 'bundler') && h.raw > 0 && !h.wallet.startsWith('player')) this.botTrade(tok, 'sell', h.raw, h.wallet, 'dev', h.tag);
  }

  private kill(tok: TokenState) {
    tok.phase = 'dead';
    for (const c of ['new', 'final', 'migrated'] as Column[]) { const i = this.cols[c].indexOf(tok.id); if (i >= 0) this.cols[c].splice(i, 1); }
    this.version++;
  }

  private genNoise(r: Rng): TokenSpec {
    const { name, ticker } = genCoinName(r);
    const id = `b${this.bgCount++}`;
    return {
      id, name, ticker, platform: r.weighted(PLAT_W), pair: r.weighted(PAIR_W), feeMode: r.weighted([[FEE_MODES[1], 44], [FEE_MODES[0], 36], [FEE_MODES[2], 20]] as const),
      art: noiseArt(r, ticker, pickNarrative(r).tag), address: fakeAddr(r, r.chance(0.6) ? 'pump' : ''), devAddress: fakeAddr(r), devLaunches: r.int(0, 60),
      fate: r.weighted(FATES), role: 'noise', devBuySol: r.chance(0.6) ? r.range(0.2, 3) : 0, sniperWave: r.chance(0.2),
      twitter: r.chance(0.5) ? `https://x.com/${ticker.toLowerCase()}` : undefined, website: r.chance(0.3) ? `https://${ticker.toLowerCase()}.example` : undefined,
    };
  }

  // ------------------------------------------------------------------ trading primitives
  /** Execute a fill against the curve. Returns [tokensOut|solOut]. Never invoked for frozen/dead tokens by bots. */
  private execute(tok: TokenState, side: 'buy' | 'sell', amount: number): { lamports: number; raw: number; fee: number } {
    if (side === 'buy') {
      const q = quoteBuy(tok.reserves, amount); tok.reserves = q.next; tok.realSolIn += amount - q.fee; tok.feesLamports += q.fee;
      return { lamports: amount, raw: q.tokensOut, fee: q.fee };
    }
    const q = quoteSell(tok.reserves, amount); tok.reserves = q.next; tok.realSolIn -= q.solOut + q.fee; tok.feesLamports += q.fee;
    return { lamports: q.solOut, raw: amount, fee: q.fee };
  }

  private record(tok: TokenState, side: 'buy' | 'sell', wallet: string, kind: Trade['kind'], f: { lamports: number; raw: number }, label?: string, tag?: Holder['tag']): Trade {
    const before = mcUsd(tok.reserves); void before;
    const h = tok.holders.get(wallet) ?? { wallet, raw: 0, tag, cost: 0 };
    if (side === 'buy') { h.cost = (h.cost ?? 0) + f.lamports; h.raw += f.raw; }
    else { const keep = h.raw > 0 ? (h.raw - f.raw) / h.raw : 0; h.cost = Math.round((h.cost ?? 0) * Math.max(0, keep)); h.raw -= f.raw; }
    if (h.raw <= 0) tok.holders.delete(wallet); else tok.holders.set(wallet, h);
    const mc = mcUsd(tok.reserves);
    const prevMc = tok.trades.length ? tok.trades[tok.trades.length - 1]!.mcUsd : mc;
    const tr: Trade = { id: `${tok.id}:${tok.txCount}`, tokenId: tok.id, t: this.time, side, wallet, kind, lamports: f.lamports, raw: f.raw, mcUsd: mc, label, tracked: kind === 'kol', impact: prevMc > 0 ? mc / prevMc - 1 : 0 };
    tok.txCount++; if (side === 'buy') tok.buys++; else tok.sells++;
    tok.volumeLamports += f.lamports;
    tok.trades.push(tr); if (tok.trades.length > SIM.TRADE_CAP) tok.trades.splice(0, tok.trades.length - SIM.TRADE_CAP);
    // KOL / pro counts derive from current holdings (complete exits decrement)
    let kc = 0, pc = 0; for (const x of tok.holders.values()) { if (x.wallet.startsWith('kol:')) kc++; else if (x.tag === 'pro') pc++; }
    tok.kolCount = kc; tok.proCount = pc;
    if (mc > tok.athMc) tok.athMc = mc;
    this.candle(tok, f.lamports, side);
    this.lifecycle(tok);
    const whale = kind === 'bot' && f.lamports >= 10 * SIM.LAMPORTS && /:W\d+$/.test(wallet);
    if (whale) tr.label = 'Whale';
    if (kind === 'kol' || kind === 'player' || kind === 'peer' || kind === 'dev' || kind === 'bundle' || whale) { this.globalTrades.push(tr); if (this.globalTrades.length > SIM.GLOBAL_TRADE_CAP) this.globalTrades.splice(0, this.globalTrades.length - SIM.GLOBAL_TRADE_CAP); }
    this.changed.add(tok.id); this.version++;
    this.emit({ type: 'trade', trade: tr });
    return tr;
  }

  /** Autonomous agent fill. Buys are capped by the agent's finite balance; sells only from held inventory. */
  private botTrade(tok: TokenState, side: 'buy' | 'sell', amount: number, wallet: string, kind: Trade['kind'], tag?: Holder['tag'], label?: string): Trade | null {
    if (tok.phase === 'migrating' || tok.phase === 'dead') return null;
    if (side === 'sell') { const h = tok.holders.get(wallet); if (!h || h.raw < amount) return null; }
    else { const bal = this.agentBalance(wallet); amount = Math.min(amount, bal); }
    if (!(amount > 0) || !Number.isFinite(amount)) return null;
    amount = Math.floor(amount);
    const f = this.execute(tok, side, amount);
    this.agentBal.set(wallet, this.agentBalance(wallet) + (side === 'buy' ? -f.lamports : f.lamports));
    return this.record(tok, side, wallet, kind, f, label, tag);
  }
  agentSol(wallet: string) { return this.agentBalance(wallet) / SIM.LAMPORTS; }

  /** KOL agent fill through the normal curve: finite balance, sells only held tokens. */
  kolTrade(tok: TokenState, side: 'buy' | 'sell', kolId: string, sol: number, fraction = 1): Trade | null {
    const k = KOL_BY_ID.get(kolId); if (!k) return null;
    const wallet = kolWallet(k.id);
    if (side === 'buy') { const f = this.flows.get(tok.id); if (f) jump(f, 0.15); return this.botTrade(tok, 'buy', solToLamports(sol), wallet, 'kol', 'kol', k.sim); }
    const h = tok.holders.get(wallet); if (!h || h.raw <= 0) return null;
    return this.botTrade(tok, 'sell', Math.max(1, Math.floor(h.raw * Math.min(1, fraction))), wallet, 'kol', 'kol', k.sim);
  }

  private candle(tok: TokenState, lamports: number, side: 'buy' | 'sell') {
    const p = priceSol(tok.reserves); const sec = Math.floor(this.time / 1000); const sol = lamports / SIM.LAMPORTS;
    const last = tok.candles[tok.candles.length - 1];
    if (last && last.t === sec) { last.h = Math.max(last.h, p); last.l = Math.min(last.l, p); last.c = p; last.v += sol; if (side === 'buy') last.bv = (last.bv ?? 0) + sol; else last.sv = (last.sv ?? 0) + sol; }
    else {
      const o = last ? last.c : p;
      tok.candles.push({ t: sec, o, h: Math.max(o, p), l: Math.min(o, p), c: p, v: sol, bv: side === 'buy' ? sol : 0, sv: side === 'sell' ? sol : 0 } satisfies Candle);
      if (tok.candles.length > SIM.CANDLE_CAP) tok.candles.splice(0, tok.candles.length - SIM.CANDLE_CAP);
    }
  }

  private lifecycle(tok: TokenState) {
    if (tok.phase === 'dead' || tok.phase === 'migrating' || tok.phase === 'migrated') return;
    const b = bonding(tok.reserves);
    if (b >= 100) {
      const from = tok.phase; tok.phase = 'migrating'; tok.migrateAt = this.time + SIM.MIGRATION_FREEZE_MS;
      this.emit({ type: 'token:phase', token: tok, from, to: 'migrating' });
      return;
    }
    if (tok.phase === 'new' && b >= SIM.FINAL_STRETCH_IN) { tok.phase = 'final'; this.moveCol(tok, 'final'); this.emit({ type: 'token:phase', token: tok, from: 'new', to: 'final' }); }
    else if (tok.phase === 'final' && b < SIM.FINAL_STRETCH_OUT) { tok.phase = 'new'; this.moveCol(tok, 'new'); this.emit({ type: 'token:phase', token: tok, from: 'final', to: 'new' }); }
  }

  private moveCol(tok: TokenState, to: Column) {
    for (const c of ['new', 'final', 'migrated'] as Column[]) { const i = this.cols[c].indexOf(tok.id); if (i >= 0) this.cols[c].splice(i, 1); }
    this.cols[to].unshift(tok.id); this.trimColumn(to); this.version++;
  }

  private trimColumn(c: Column) {
    const cap = SIM.COLUMN_CAP[c] + (c === 'new' ? 20 : 0);
    while (this.cols[c].length > cap) {
      // drop the oldest card that is not part of an active scenario
      let idx = -1;
      for (let i = this.cols[c].length - 1; i >= 0; i--) { const t = this.tokens.get(this.cols[c][i]!); if (t && t.role === 'noise') { idx = i; break; } }
      if (idx < 0) idx = this.cols[c].length - 1;
      this.cols[c].splice(idx, 1);
    }
  }

  private pruneTokens() {
    if (this.tokens.size <= SIM.MAX_TOKENS) return;
    const inCols = new Set([...this.cols.new, ...this.cols.final, ...this.cols.migrated]);
    for (const [id, t] of this.tokens) {
      if (this.tokens.size <= SIM.MAX_TOKENS * 0.9) break;
      if (inCols.has(id) || t.role !== 'noise' || this.positions.get(id)?.raw || this.positions.get(id)?.reservedRaw) continue;
      this.tokens.delete(id); this.fp.delete(id); this.tokRng.delete(id); this.positions.delete(id); this.flows.delete(id);
    }
  }

  // ------------------------------------------------------------------ player orders
  position(tokenId: string): Position {
    let p = this.positions.get(tokenId);
    if (!p) { p = { tokenId, raw: 0, reservedRaw: 0, costLamports: 0, realizedLamports: 0, feesLamports: 0, buys: 0, sells: 0 }; this.positions.set(tokenId, p); }
    return p;
  }

  /** Validate + reserve, then queue for landing at now+latency. Returns the order (status may be 'rejected'). */
  placeOrder(o: { side: 'buy' | 'sell'; tokenId: string; lamports?: number; raw?: number; roundId?: string; latencyMs?: number; at?: number; slippageBps?: number }): Order {
    const id = `o:${this.orderSeq++}`;
    const at = o.at ?? this.time;
    const order: Order = { id, side: o.side, tokenId: o.tokenId, lamports: 0, raw: 0, submittedAt: at, landAt: at + (o.latencyMs ?? this.latencyMs), status: 'pending', roundId: o.roundId };
    const tok = this.tokens.get(o.tokenId);
    if (tok) order.clickMcUsd = mcUsd(tok.reserves);
    const reject = (reason: string) => { order.status = 'rejected'; order.reason = reason; this.log(order); this.emit({ type: 'order', order }); return order; };
    if (!tok) return reject('unknown token');
    if (tok.phase === 'dead') return reject('token is no longer trading');
    if (o.side === 'buy') {
      const l = o.lamports ?? NaN;
      if (!isInt(l) || l <= 0) return reject('invalid amount');
      if (l > this.balance) return reject('insufficient balance');
      this.balance -= l; this.reserved += l; order.lamports = l;
    } else {
      const r = o.raw ?? NaN; const pos = this.positions.get(o.tokenId);
      if (!isInt(r) || r <= 0) return reject('invalid amount');
      if (!pos || pos.raw - pos.reservedRaw < r) return reject('insufficient tokens');
      pos.reservedRaw += r; order.raw = r;
    }
    if (o.slippageBps != null && o.slippageBps >= 0) {
      const q = o.side === 'buy' ? quoteBuy(tok.reserves, order.lamports).tokensOut : quoteSell(tok.reserves, order.raw).solOut;
      order.minOut = Math.floor(q * (1 - Math.min(10_000, o.slippageBps) / 10_000));
    }
    this.orders.push(order);
    this.emit({ type: 'order', order }); this.emit({ type: 'wallet' });
    if (this.remote?.tokens.has(o.tokenId)) this.remote.submit(order);
    return order;
  }

  // ------------------------------------------------------------------ simulated limit orders
  /** Reserve now; when MC crosses the trigger (checked each tick) a market order enters the latency queue and fills at
   *  the landing price. A limit guarantees the trigger, not an exact execution price. */
  placeLimit(o: { side: 'buy' | 'sell'; tokenId: string; lamports?: number; raw?: number; triggerMcUsd: number }): LimitOrder | string {
    const tok = this.tokens.get(o.tokenId); if (!tok || tok.phase === 'dead') return 'unknown token';
    if (!Number.isFinite(o.triggerMcUsd) || o.triggerMcUsd <= 0) return 'invalid trigger';
    const lo: LimitOrder = { id: `l:${this.orderSeq++}`, side: o.side, tokenId: o.tokenId, lamports: 0, raw: 0, triggerMcUsd: o.triggerMcUsd, createdAt: this.time, status: 'open' };
    if (o.side === 'buy') { const l = o.lamports ?? NaN; if (!isInt(l) || l <= 0) return 'invalid amount'; if (l > this.balance) return 'insufficient balance'; this.balance -= l; this.reserved += l; lo.lamports = l; }
    else { const r = o.raw ?? NaN; const pos = this.positions.get(o.tokenId); if (!isInt(r) || r <= 0) return 'invalid amount'; if (!pos || pos.raw - pos.reservedRaw < r) return 'insufficient tokens'; pos.reservedRaw += r; lo.raw = r; }
    this.limits.push(lo); this.emit({ type: 'wallet' }); this.version++;
    return lo;
  }
  cancelLimit(id: string) {
    const lo = this.limits.find((x) => x.id === id); if (!lo || lo.status !== 'open') return;
    this.limits = this.limits.filter((x) => x !== lo); lo.status = 'cancelled';
    if (lo.side === 'buy') this.release(lo.lamports); else { const p = this.positions.get(lo.tokenId); if (p) p.reservedRaw -= lo.raw; }
    this.version++;
  }
  private checkLimits() {
    for (const lo of [...this.limits]) {
      const tok = this.tokens.get(lo.tokenId); if (!tok) continue;
      const mc = mcUsd(tok.reserves);
      if (lo.side === 'buy' ? mc > lo.triggerMcUsd : mc < lo.triggerMcUsd) continue;
      this.limits = this.limits.filter((x) => x !== lo); lo.status = 'triggered';
      // reservation carries over into the queued market order (no double reservation)
      const order: Order = { id: `o:${this.orderSeq++}`, side: lo.side, tokenId: lo.tokenId, lamports: lo.lamports, raw: lo.raw, submittedAt: this.time, landAt: this.time + this.latencyMs, status: 'pending' };
      lo.orderId = order.id; this.orders.push(order); this.emit({ type: 'order', order });
      if (this.remote?.tokens.has(order.tokenId)) this.remote.submit(order);
    }
  }

  cancelOrder(id: string) { const o = this.orders.find((x) => x.id === id); if (o) this.finishOrder(o, 'cancelled', 'cancelled by user'); }

  private release(lamports: number) { this.reserved -= lamports; this.balance += lamports; this.emit({ type: 'wallet' }); }

  private finishOrder(o: Order, status: 'rejected' | 'cancelled' | 'filled', reason?: string) {
    this.orders = this.orders.filter((x) => x !== o);
    if (status !== 'filled') {
      if (o.side === 'buy') this.release(o.lamports);
      else { const p = this.positions.get(o.tokenId); if (p) p.reservedRaw -= o.raw; }
    }
    o.status = status; o.reason = reason; this.log(o);
    this.emit({ type: 'order', order: o }); this.emit({ type: 'wallet' }); this.version++;
  }

  private log(o: Order) { this.orderLog.push(o); if (this.orderLog.length > 200) this.orderLog.splice(0, this.orderLog.length - 200); }

  private settle(o: Order) {
    const tok = this.tokens.get(o.tokenId);
    if (!tok || tok.phase === 'dead') return this.finishOrder(o, 'rejected', 'token is no longer trading');
    if (o.minOut != null) {
      const out = o.side === 'buy' ? quoteBuy(tok.reserves, o.lamports).tokensOut : quoteSell(tok.reserves, o.raw).solOut;
      if (out < o.minOut && tok.phase !== 'migrating') return this.finishOrder(o, 'rejected', 'slippage exceeded at landing (refunded)');
    }
    if (tok.phase === 'migrating') return this.finishOrder(o, 'rejected', `migrating: fills paused for ${Math.max(0, Math.ceil(((tok.migrateAt ?? this.time) - this.time) / 100) / 10)}s`);
    this.applyOwnFill(o, tok, null);
  }

  /** Apply the player's fill (local, or server-provided amounts in multiplayer). */
  applyOwnFill(o: Order, tok: TokenState, server: { lamports: number; raw: number } | null) {
    const pos = this.position(o.tokenId);
    o.preFillMcUsd = mcUsd(tok.reserves);
    if (o.side === 'buy') {
      const f = server ? (() => { const q = quoteBuy(tok.reserves, o.lamports); return { lamports: o.lamports, raw: server.raw, fee: q.fee, next: q }; })() : null;
      const fill = f ? (tok.reserves = f.next.next, tok.realSolIn += o.lamports - f.fee, tok.feesLamports += f.fee, { lamports: o.lamports, raw: f.raw, fee: f.fee }) : this.execute(tok, 'buy', o.lamports);
      this.reserved -= o.lamports;
      pos.raw += fill.raw; pos.costLamports += fill.lamports; pos.feesLamports += fill.fee; pos.buys++;
      o.fillRaw = fill.raw; o.fillLamports = fill.lamports;
      this.record(tok, 'buy', 'player', 'player', fill, 'You', 'player');
    } else {
      pos.reservedRaw -= o.raw;
      const fill = server ? (() => { const q = quoteSell(tok.reserves, o.raw); tok.reserves = q.next; tok.realSolIn -= q.solOut + q.fee; tok.feesLamports += q.fee; return { lamports: server.lamports, raw: o.raw, fee: q.fee }; })() : this.execute(tok, 'sell', o.raw);
      const costOut = pos.raw > 0 ? Math.round((pos.costLamports * fill.raw) / pos.raw) : 0;
      pos.raw -= fill.raw; pos.costLamports -= costOut; pos.realizedLamports += fill.lamports - costOut; pos.feesLamports += fill.fee; pos.sells++;
      this.balance += fill.lamports;
      o.fillRaw = fill.raw; o.fillLamports = fill.lamports;
      this.record(tok, 'sell', 'player', 'player', fill, 'You', 'player');
    }
    o.postFillMcUsd = mcUsd(tok.reserves);
    this.finishOrder(o, 'filled');
  }

  /** Multiplayer: server confirmed or rejected a forwarded order. */
  remoteResult(orderId: string, r: { ok: true; lamports: number; raw: number } | { ok: false; reason: string }) {
    const o = this.orders.find((x) => x.id === orderId); if (!o) return;
    const tok = this.tokens.get(o.tokenId);
    if (!r.ok || !tok) return this.finishOrder(o, 'rejected', r.ok ? 'unknown token' : r.reason);
    this.applyOwnFill(o, tok, { lamports: r.lamports, raw: r.raw });
  }

  /** Multiplayer: a peer's fill moves the shared token. Sells are capped by the peer's bag. */
  peerFill(tokenId: string, pid: string, name: string, side: 'buy' | 'sell', amount: number): Trade | null {
    const tok = this.tokens.get(tokenId); if (!tok || tok.phase === 'migrating' || tok.phase === 'dead') return null;
    const wallet = 'peer:' + pid;
    if (side === 'sell') { const h = tok.holders.get(wallet); amount = Math.min(amount, h?.raw ?? 0); if (amount <= 0) return null; }
    if (!isInt(amount) || amount <= 0) return null;
    const f = this.execute(tok, side, amount);
    return this.record(tok, side, wallet, 'peer', f, name);
  }

  /** Multiplayer: overwrite canonical reserves for a token (server checkpoint). */
  checkpoint(tokenId: string, reserves: Reserves) {
    const tok = this.tokens.get(tokenId); if (!tok) return;
    tok.reserves = { ...reserves, feeBps: reserves.feeBps ?? tok.reserves.feeBps }; this.changed.add(tokenId); this.version++; this.lifecycle(tok);
  }

  // ------------------------------------------------------------------ deploy (atomic)
  deploy(req: DeployRequest, opts: { roundId?: string; latencyMs?: number; at?: number } = {}): { ok: boolean; reason?: string; id: string; landAt: number } {
    const id = `d:${this.orderSeq++}`; const at = opts.at ?? this.time; const landAt = at + (opts.latencyMs ?? this.latencyMs);
    const err = validateDeploy(req);
    if (err) return { ok: false, reason: err, id, landAt };
    const total = solToLamports(req.devBuySol) + req.bundle.reduce((s, b) => s + solToLamports(b), 0) + solToLamports(SIM.DEPLOY_FEE_SOL);
    if (total > this.balance) return { ok: false, reason: 'insufficient balance', id, landAt };
    this.balance -= total; this.reserved += total;
    this.deploys.push({ id, req, total, submittedAt: at, landAt, roundId: opts.roundId, seq: this.orderSeq });
    this.emit({ type: 'wallet' });
    return { ok: true, id, landAt };
  }

  private landDeploy(d: PendingDeploy) {
    this.deploys = this.deploys.filter((x) => x !== d);
    const r = this.walletRng.derive(d.id);
    const tid = `p${d.id.slice(2)}`;
    const spec: TokenSpec = { ...d.req, id: tid, address: fakeAddr(r, d.req.vanity ?? 'pump'), devAddress: 'player', devLaunches: 0, fate: 'weak', role: 'player', devBuySol: 0 };
    const tok = this.createToken(spec, this.time);
    // all fills in one synchronous step = atomic; on any failure everything is rolled back
    const snapshot = { reserves: { ...tok.reserves }, bal: this.balance, reserved: this.reserved };
    try {
      const pos = this.position(tid);
      const feeL = solToLamports(SIM.DEPLOY_FEE_SOL);
      const buys = [{ w: 'dev', sol: d.req.devBuySol, kind: 'dev' as const, tag: 'dev' as const }, ...d.req.bundle.map((b, i) => ({ w: `bundle${i}`, sol: b, kind: 'bundle' as const, tag: 'bundler' as const }))];
      for (const b of buys) {
        const l = solToLamports(b.sol); if (l <= 0) continue;
        const f = this.execute(tok, 'buy', l);
        pos.raw += f.raw; pos.costLamports += f.lamports; pos.feesLamports += f.fee; pos.buys++;
        this.record(tok, 'buy', `player:${b.w}`, b.kind, f, b.kind === 'dev' ? 'Your dev wallet' : `Your bundle ${b.w.slice(6)}`, b.tag);
      }
      pos.feesLamports += feeL;
      pos.costLamports += feeL;
      this.reserved -= d.total;
      const p = this.fp.get(tid); if (p) p.m0 = mcUsd(tok.reserves);
      d.tokenId = tid;
      this.emit({ type: 'deploy', ok: true, token: tok, requestId: d.id, landAt: d.landAt, roundId: d.roundId });
    } catch (e) {
      tok.reserves = snapshot.reserves; this.balance = snapshot.bal; this.reserved = snapshot.reserved; this.release(d.total); this.kill(tok);
      this.emit({ type: 'deploy', ok: false, reason: 'deploy failed: ' + String(e), requestId: d.id, landAt: d.landAt, roundId: d.roundId });
    }
    this.emit({ type: 'wallet' });
  }

  // ------------------------------------------------------------------ queries
  pnl(tokenId: string): { valueLamports: number; unrealized: number; realized: number; cost: number } {
    const p = this.positions.get(tokenId); const tok = this.tokens.get(tokenId);
    if (!p) return { valueLamports: 0, unrealized: 0, realized: 0, cost: 0 };
    let value = 0;
    if (tok && p.raw > 0) { try { value = quoteSell(tok.reserves, p.raw).solOut; } catch { value = 0; } }
    return { valueLamports: value, unrealized: value - p.costLamports, realized: p.realizedLamports, cost: p.costLamports };
  }
  totals() {
    let value = 0, unreal = 0, real = 0, cost = 0;
    for (const id of this.positions.keys()) { const x = this.pnl(id); value += x.valueLamports; unreal += x.unrealized; real += x.realized; cost += x.cost; }
    return { value, unreal, real, cost, balance: this.balance, reserved: this.reserved };
  }
  setBalance(sol: number) { this.balance = solToLamports(sol); this.emit({ type: 'wallet' }); this.version++; }
  /** Conservation check for tests: real SOL in curve equals net SOL bought minus sold (excl. fees). */
  conservation(tokenId: string) {
    const tok = this.tokens.get(tokenId)!; let held = 0; for (const h of tok.holders.values()) held += h.raw;
    const sold = tok.reserves.feeBps === SIM.AMM_FEE_BPS ? SIM.TOTAL_SUPPLY_RAW - tok.reserves.vTok : INITIAL.vTok - tok.reserves.vTok;
    return { sold, held };
  }
  mc(tokenId: string) { const t = this.tokens.get(tokenId); return t ? mcUsd(t.reserves) : 0; }
}

export function validateDeploy(req: DeployRequest): string | null {
  if (!req.name.trim() || req.name.length > SIM.NAME_MAX) return `name must be 1-${SIM.NAME_MAX} characters`;
  if (!req.ticker.trim() || req.ticker.length > SIM.TICKER_MAX) return `ticker must be 1-${SIM.TICKER_MAX} characters`;
  if (!PLATFORMS.includes(req.platform)) return 'unknown launchpad';
  if (!Number.isFinite(req.devBuySol) || req.devBuySol < 0 || req.devBuySol > 500) return 'invalid dev buy';
  if (req.bundle.length > 8 || req.bundle.some((b) => !Number.isFinite(b) || b < 0 || b > 200)) return 'invalid bundle';
  return null;
}
